import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  type AgentRecordContext, useListAgentSharedAttachments, getListAgentSharedAttachmentsQueryKey, getListAgentSharedAttachmentMembersQueryKey,
  useUploadAgentSharedAttachment, useProcessAgentSharedAttachment, useRemoveAgentSharedAttachment, getAgentSharedAttachmentUpload, getAgentSharedAttachment,
  ApiError, type AgentAccessScope, type AgentAttachment, type AgentAttachmentReference,
  type AgentAttachmentUpload, type AgentConversationCreate,
  getListAgentAttachmentsQueryKey, getListAgentAttachmentMembersQueryKey,
  getListAgentConversationsQueryKey, useListAgentAttachments,
  useCreateAgentConversation, useUploadAgentAttachment, useProcessAgentAttachment,
  useRemoveAgentAttachment, getAgentAttachmentUpload, getAgentAttachment
} from '@cdf/api-client-react';
import { agentWorkspaceParams, workspaceError } from './workspace-utils';

const MAX_BYTES = 20 * 1024 * 1024;
export const ATTACHMENT_ACCEPT = '.zip,.pdf,.docx,.xlsx,.pptx,.txt,.md,.csv,.json,.html,.htm';
export type UploadTask = {
  key: string; file: File; phase: 'queued' | 'reading' | 'uploading' | 'processing' | 'uncertain';
  payload?: AgentAttachmentUpload; attachment?: AgentAttachment; error?: string;
};
type Options = {
  scope: AgentAccessScope | null; conversationId?: string; identity: string; eligible: boolean;
  onConversation: (id: string) => void; sharedContext?: AgentRecordContext;
};
function readBase64(file: File, signal: AbortSignal): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const cancel = () => reader.abort();
    signal.addEventListener('abort', cancel, { once: true });
    reader.onload = () => { signal.removeEventListener('abort', cancel); resolve(String(reader.result).split(',')[1] ?? ''); };
    reader.onerror = reader.onabort = () => { signal.removeEventListener('abort', cancel); reject(new Error('File reading stopped. Choose the file again.')); };
    reader.readAsDataURL(file);
  });
}
function terminal(row: AgentAttachment) { return ['ready', 'partial', 'unsupported', 'removed'].includes(row.status); }
export function attachmentReady(row: AgentAttachment) {
  return (row.status === 'ready' || row.status === 'partial') && Boolean(row.extraction);
}
function samePin(a: AgentAttachment, b: AgentAttachment) {
  return a.id === b.id && a.revision === b.revision && a.contentHash === b.contentHash;
}

export function useAgentAttachments({ scope, conversationId, identity, eligible, onConversation, sharedContext }: Options) {
  const client = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const [offset, setOffset] = React.useState(0);
  const [tasks, setTasks] = React.useState<UploadTask[]>([]);
  const [selected, setSelected] = React.useState<AgentAttachment[]>([]);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string>();
  const [notice, setNotice] = React.useState<string>();
  const [removing, setRemoving] = React.useState<AgentAttachment>();
  const flight = React.useRef(false);
  const generation = React.useRef(0);
  const controller = React.useRef(new AbortController());
  const mounted = React.useRef(true);
  const reservation = React.useRef<AgentConversationCreate | undefined>(undefined);
  const reservedId = React.useRef<string | undefined>(undefined);
  const taskRef = React.useRef(tasks); taskRef.current = tasks;
  const selectionRef = React.useRef(selected); selectionRef.current = selected;
  const params = agentWorkspaceParams(scope ?? { kind: 'entity' });
  const pageParams = { ...params, limit: 10, offset };
  const sharedParams = { tenantId: sharedContext?.tenantId ?? '', entityId: sharedContext?.entityId ?? '', contextKind: sharedContext?.kind ?? 'campaign' as const, recordId: sharedContext?.recordId ?? '' };
  const sharedPage = { ...sharedParams, limit: 10, offset };
  const sharedQuery = useListAgentSharedAttachments(conversationId ?? '', sharedPage, { query: {
    queryKey: [...getListAgentSharedAttachmentsQueryKey(conversationId ?? '', sharedPage), identity], enabled: eligible && Boolean(sharedContext && conversationId), retry: false, staleTime: 0
  } });
  const personalQuery = useListAgentAttachments(conversationId ?? '', pageParams, { query: {
    queryKey: [...getListAgentAttachmentsQueryKey(conversationId ?? '', pageParams), identity],
    enabled: eligible && !sharedContext && Boolean(scope && conversationId), retry: false, staleTime: 0
  } });
  const query = sharedContext ? sharedQuery : personalQuery;
  const request = { get signal() { return controller.current.signal; } };
  const sharedUpload = useUploadAgentSharedAttachment({ mutation: { retry: false }, request });
  const sharedProcess = useProcessAgentSharedAttachment({ mutation: { retry: false }, request });
  const sharedRemove = useRemoveAgentSharedAttachment({ mutation: { retry: false }, request });
  const create = useCreateAgentConversation({ mutation: { retry: false }, request });
  const upload = useUploadAgentAttachment({ mutation: { retry: false }, request });
  const process = useProcessAgentAttachment({ mutation: { retry: false }, request });
  const remove = useRemoveAgentAttachment({ mutation: { retry: false }, request });
  React.useEffect(() => {
    mounted.current = true;
    if (controller.current.signal.aborted) controller.current = new AbortController();
    return () => { mounted.current = false; generation.current++; controller.current.abort(); };
  }, []);
  React.useEffect(() => {
    if (query.data && offset > 0 && offset >= query.data.total) setOffset(Math.max(0, Math.floor((query.data.total - 1) / 10) * 10));
  }, [query.data, offset]);

  function reset() {
    generation.current++; controller.current.abort(); controller.current = new AbortController();
    flight.current = false; setBusy(false); setTasks([]); taskRef.current = [];
    setSelected([]); selectionRef.current = []; setOffset(0); setOpen(false);
    setError(undefined); setNotice(undefined); setRemoving(undefined);
    reservation.current = undefined; reservedId.current = undefined;
  }
  function updateTask(key: string, patch: Partial<UploadTask>) {
    setTasks(current => { const next = current.map(t => t.key === key ? { ...t, ...patch } : t); taskRef.current = next; return next; });
  }
  function cancelUploads() {
    if (!flight.current || !taskRef.current.length) return;
    generation.current++; controller.current.abort(); controller.current = new AbortController();
    flight.current = false; setBusy(false);
    setTasks(current => { const next = current.map(t => ({ ...t, phase: 'uncertain' as const, error: 'Stopped waiting. The server may have saved the file. Check the original upload before retrying or exclude it.' })); taskRef.current = next; return next; });
  }
  function forget(key: string) {
    if (flight.current) return;
    setTasks(current => { const next = current.filter(t => t.key !== key); taskRef.current = next; return next; });
    setNotice('Excluded from this message. A saved upload may still be in this conversation; refresh files to check it.');
  }
  async function refresh(id = conversationId, attachmentId?: string) {
    if (!id) return;
    await client.invalidateQueries({ queryKey: sharedContext ? getListAgentSharedAttachmentsQueryKey(id) : getListAgentAttachmentsQueryKey(id) });
    if (attachmentId) await client.invalidateQueries({ queryKey: (sharedContext ? getListAgentSharedAttachmentMembersQueryKey(id, attachmentId) : getListAgentAttachmentMembersQueryKey(id, attachmentId)) });
  }
  async function perform(task: UploadTask, id: string, token: number, recovering = false) {
    const active = () => mounted.current && generation.current === token;
    let row = task.attachment;
    if (recovering) {
      // Read the original immutable reservation before attempting any retry.
      try { row = await (sharedContext ? getAgentSharedAttachmentUpload(id, task.key, sharedParams, request) : getAgentAttachmentUpload(id, task.key, params, request)); }
      catch (failure) { if (!(failure instanceof ApiError && failure.status === 404)) throw failure; }
      if (!active()) return;
    }
    if (!task.payload) updateTask(task.key, { phase: 'reading' });
    const payload = task.payload ?? { accessScope: scope!, uploadId: task.key, filename: task.file.name,
      contentType: task.file.type || 'application/octet-stream', dataBase64: await readBase64(task.file, controller.current.signal) };
    if (!active()) return;
    updateTask(task.key, { payload, phase: 'uploading' });
    if (!row || row.status === 'uploading' || (row.status === 'failed' && row.failureCode === 'AGENT_ATTACHMENT_STORAGE_FAILED')) {
      row = sharedContext ? await sharedUpload.mutateAsync({ id, data: { context: sharedContext, acknowledgeSharing: true, uploadId: payload.uploadId, filename: payload.filename, contentType: payload.contentType, dataBase64: payload.dataBase64 } }) : await upload.mutateAsync({ id, data: payload });
      if (!active()) return;
      updateTask(task.key, { attachment: row });
    }
    if (!terminal(row)) {
      updateTask(task.key, { phase: 'processing', attachment: row });
      row = sharedContext ? await sharedProcess.mutateAsync({ id, attachmentId: row.id, data: { context: sharedContext } }) : await process.mutateAsync({ id, attachmentId: row.id, data: { accessScope: scope! } });
      if (!active()) return;
    }
    if (row.conversationId !== id || row.uploadId !== task.key) throw new Error('The upload returned a different file identity. Check files before continuing.');
    if (!terminal(row)) {
      updateTask(task.key, { attachment: row, phase: 'uncertain', error: row.status === 'processing'
        ? 'Still processing. Check the same upload; abandoned processing can be retried after two minutes.'
        : 'File is not ready. Check this upload before retrying.' });
    } else {
      setTasks(current => { const next = current.filter(t => t.key !== task.key); taskRef.current = next; return next; });
      if (row.status === 'ready') {
        if (selectionRef.current.length >= 10) setNotice(row.filename + ' is ready but not included: the message already has 10 selected files.');
        const ready = row;
        setSelected(current => { const next = current.length < 10 && !current.some(s => s.id === ready.id) ? [...current, ready] : current; selectionRef.current = next; return next; });
      } else {
        setNotice(row.filename + ' is ' + row.status + ' and is not included. Review its warnings before selecting, or continue without it.');
      }
    }
    await refresh(id, row.id);
  }
  async function run(batch: UploadTask[], recovering = false) {
    if (flight.current || !eligible || !scope) return;
    flight.current = true; setBusy(true); setError(undefined); setNotice(undefined);
    const token = generation.current;
    const active = () => mounted.current && generation.current === token;
    try {
      let id = conversationId ?? reservedId.current;
      if (!id && sharedContext) throw new Error('Choose an existing record-shared thread before adding files.');
      if (!id) {
        reservation.current ??= { clientConversationId: crypto.randomUUID(), title: 'New chat', mode: 'internal', accessScope: scope };
        const row = await create.mutateAsync({ data: reservation.current });
        if (!active()) return;
        if (row.id !== reservation.current.clientConversationId) throw new Error('The conversation reservation changed. Check saved chats before continuing.');
        id = row.id; reservedId.current = id; onConversation(id);
        void client.invalidateQueries({ queryKey: getListAgentConversationsQueryKey() });
      }
      for (const task of batch) {
        if (!active()) return;
        try { await perform(task, id, token, recovering); }
        catch (failure) { if (active()) updateTask(task.key, { phase: 'uncertain', error: workspaceError(failure) }); }
      }
    } catch (failure) {
      if (active()) {
        setError(workspaceError(failure));
        for (const task of batch) updateTask(task.key, { phase: 'uncertain', error: 'Conversation reservation is unresolved. Check/retry uses the same conversation and upload IDs.' });
      }
    } finally {
      if (active()) { flight.current = false; setBusy(false); }
    }
  }
  function add(files: File[]) {
    if (flight.current || !eligible) return;
    setOpen(true); setError(undefined);
    if (!files.length) return;
    if (files.length + tasks.length > 10 || files.reduce((sum, f) => sum + f.size, 0) > 100 * 1024 * 1024) {
      setError('Choose up to 10 files at a time, no more than 100 MiB combined. Finish or exclude existing uploads first.'); return;
    }
    if (files.some(file => !file.size || file.size > MAX_BYTES || !file.name.trim() || file.name.length > 255 || /[\\/\u0000-\u001f]/.test(file.name))) {
      setError('Each file needs a valid filename and must be between 1 byte and 20 MiB. No files in this selection were uploaded.'); return;
    }
    const batch: UploadTask[] = files.map(file => ({ key: crypto.randomUUID(), file, phase: 'queued' }));
    setTasks(current => { const next = [...current, ...batch]; taskRef.current = next; return next; });
    void run(batch);
  }
  function choose(row: AgentAttachment, include: boolean) {
    if (flight.current) return;
    if (include && !attachmentReady(row)) return;
    if (include && selected.length >= 10) { setError('Include at most 10 original files or ZIPs per message.'); return; }
    setSelected(current => { const next = include
      ? [...current.filter(s => s.id !== row.id && s.id !== row.parentId && s.parentId !== row.id), row]
      : current.filter(s => s.id !== row.id); selectionRef.current = next; return next; });
  }
  async function retry(row: AgentAttachment) {
    if (flight.current || !scope || !conversationId) return;
    flight.current = true; setBusy(true); setError(undefined);
    const token = generation.current;
    try {
      const fresh = await (sharedContext ? getAgentSharedAttachment(conversationId, row.id, sharedParams, request) : getAgentAttachment(conversationId, row.id, params, request));
      if (!mounted.current || token !== generation.current) return;
      if (!terminal(fresh)) {
        if (sharedContext) await sharedProcess.mutateAsync({ id: conversationId, attachmentId: row.id, data: { context: sharedContext } });
        else await process.mutateAsync({ id: conversationId, attachmentId: row.id, data: { accessScope: scope } });
      }
      if (mounted.current && token === generation.current) await refresh();
    } catch (failure) { if (mounted.current && token === generation.current) setError(workspaceError(failure)); }
    finally { if (mounted.current && token === generation.current) { flight.current = false; setBusy(false); } }
  }
  async function confirmRemove() {
    if (flight.current || !removing || !conversationId) return;
    flight.current = true; setBusy(true); setError(undefined);
    const token = generation.current;
    try {
      const row = sharedContext ? await sharedRemove.mutateAsync({ id: conversationId, attachmentId: removing.id, params: sharedParams }) : await remove.mutateAsync({ id: conversationId, attachmentId: removing.id, params });
      if (!mounted.current || token !== generation.current) return;
      chooseAfterRemoval(row.id); setRemoving(row.cleanupPending ? row : undefined);
      setNotice(row.cleanupPending ? 'Removed from chat; byte cleanup is pending. Retry cleanup for this same file.' : 'Removed uncited file from this conversation.');
      await refresh();
    } catch (failure) { if (mounted.current && token === generation.current) setError(workspaceError(failure)); }
    finally { if (mounted.current && token === generation.current) { flight.current = false; setBusy(false); } }
  }
  function chooseAfterRemoval(id: string) {
    setSelected(current => { const next = current.filter(s => s.id !== id && s.parentId !== id); selectionRef.current = next; return next; });
  }
  const invalidSelection = selected.some(s => !attachmentReady(s) || query.data?.items.some(row => row.id === s.id && (!samePin(row, s) || !attachmentReady(row))));
  const blocked = busy || tasks.length > 0 || invalidSelection || (selected.length > 0 && (query.isError || query.isFetching));
  function canNavigate() { return !flight.current && taskRef.current.length === 0; }
  function canSend() { return !flight.current && taskRef.current.length === 0 && !blocked; }
  function pins(): AgentAttachmentReference[] { return selectionRef.current.map(({ id, revision, contentHash }) => ({ id, revision, contentHash })); }
  return { sharedContext, open, setOpen, offset, setOffset, query, params, selected, tasks, busy, error, notice, removing,
    setRemoving, add, choose, retry, confirmRemove, refresh, reset, forget, run, cancelUploads, blocked, canSend, canNavigate, pins,
    hasSelection: selected.length > 0, pending: busy, hasDraft: selected.length > 0 || tasks.length > 0 };
}
export type AgentAttachmentsController = ReturnType<typeof useAgentAttachments>;
